import React, {
  Dispatch, SetStateAction, useEffect, useState,
} from 'react';
import {
  Text, StyleSheet, FlatList, Pressable, View, ScrollView, ViewStyle,
} from 'react-native';
import { FontAwesome } from '@expo/vector-icons';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigation } from '@react-navigation/native';
import Colors from '../../constants/Colors';
import PressableStyled, { PressableState } from '../PressableStyled';
import Style from '../../constants/Style';
import {
  Area, Floor, AreaType, LocationSubtypeTitles, Space, Region,
} from '../Map/SetFloorsModal/FloorItem';
import ScrollViewStyled from '../ScrollViewStyled';
import {
  AssetInstance, MeterLabel, SensorLabel, TemplateType,
} from '../../constants/Types';
import getAssets from '../../requests/Assets/GetAssets';
import GetEntireLocationStructure from '../GetEntireLocationStructure';
import { updateLocations } from '../../redux/locationStructureSlice';
import { Building } from '../../screens/LocationManagement/components/AddBuildingModal';

interface LocationSelectorProps {
  selectedSensorId?: string,
  setSelectedSensorId?: Dispatch<SetStateAction<string>>,
  setSelectedSensorName?: Dispatch<SetStateAction<string>>,
  setLocationName?: Dispatch<SetStateAction<string>>,
  setLocationType?: Dispatch<SetStateAction<AreaType | null>>,
  setLocationCapacity?: Dispatch<SetStateAction<number | null>>,
  setSelectedExternalId?: Dispatch<SetStateAction<string>>,
  selectedBuildingId: string,
  setSelectedBuildingId?: Dispatch<SetStateAction<string>>,
  selectedFloorId: string,
  setSelectedFloorId: Dispatch<SetStateAction<string>>,
  selectedAreaId: string,
  setSelectedAreaId: Dispatch<SetStateAction<string>>,
  selectedSpaceId: string,
  setSelectedSpaceId: Dispatch<SetStateAction<string>>,
  hideOtherBuildings?: boolean,
  sensorsShown?: boolean,
  hideBorders?: boolean,
  sensorTypesShown?: SensorLabel[],
  setLocationHasSensors?: Dispatch<SetStateAction<boolean>>,
}

const LocationSelector = ({
  setLocationName, selectedBuildingId, setSelectedBuildingId, selectedFloorId, setSelectedFloorId,
  selectedAreaId, setSelectedAreaId, selectedSpaceId, setSelectedSpaceId, setLocationType,
  setSelectedExternalId, hideOtherBuildings = true, setLocationCapacity, sensorsShown,
  selectedSensorId, setSelectedSensorId, setSelectedSensorName, hideBorders, sensorTypesShown,
  setLocationHasSensors,
}: LocationSelectorProps) => {
  const navigation = useNavigation();
  const dispatch = useDispatch();
  const currentBuildingId: string = useSelector((state:
    { selectedBuilding: { id: string } }) => state.selectedBuilding.id);
  const locations = useSelector((state: { locations }) => state.locations.buildings);
  const [expandedBuildingId, setExpandedBuildingId] = useState<string | null>(null);
  const [expandedFloorId, setExpandedFloorId] = useState<string | null>(null);
  const [expandedAreaId, setExpandedAreaId] = useState<string | null>(null);
  const [sensors, setSensors] = useState<AssetInstance[]>([]);

  const itemContainerSelectedStyle: ViewStyle = {
    borderColor: hideBorders ? 'transparent' : Colors.light.hereworksBlue100,
    backgroundColor: Colors.light.hereworksWhite300,
  };

  const selectedBuilding: Building | null = (selectedBuildingId && locations)
    ? locations[selectedBuildingId] : null;
  const selectedFloor: Floor = (selectedBuilding && selectedFloorId)
    ? selectedBuilding.floors[selectedFloorId] : null;
  const selectedArea: Area = (selectedFloor && selectedAreaId)
    ? selectedFloor.areas[selectedAreaId] : null;

  const selectedSpace: Space = (selectedArea && selectedSpaceId)
    ? selectedArea.spaces[selectedSpaceId] : null;
  const selectedLocation: Building | Region = selectedSpace ?? selectedArea ?? selectedFloor
    ?? selectedBuilding;

  const mergeAssetCounts = (
    counts: Record<SensorLabel | MeterLabel, number>[],
  ): Record<SensorLabel | MeterLabel, number> => counts.reduce((acc, current) => {
    if (!current) return acc;
    Object.keys(current).forEach((key) => {
      acc[key] = (acc[key] || 0) + current[key];
    });
    return acc;
  }, {} as Record<SensorLabel | MeterLabel, number>);

  const getSensorCount = (assetCountByLabel: Record<SensorLabel | MeterLabel, number>) => Object
    .keys(assetCountByLabel ?? {})
    .reduce((total: number, current: SensorLabel) => {
      if (sensorTypesShown?.includes(current)) {
        return total + assetCountByLabel[current];
      }
      return total;
    }, 0);

  useEffect(() => {
    setExpandedBuildingId(selectedBuildingId);
    setExpandedFloorId(selectedFloorId);
    setExpandedAreaId(selectedAreaId);
  }, [selectedBuildingId, selectedFloorId, selectedAreaId, selectedSpaceId]);

  useEffect(() => {
    if (setSelectedBuildingId) setSelectedBuildingId(currentBuildingId);
    // setSelectedFloorId(null);
    // setSelectedAreaId(null);
    // setSelectedSpaceId(null);
    // if (setSelectedSensorId) setSelectedSensorId(null);
    // if (setSelectedSensorName) setSelectedSensorName(null);
    if (setLocationType) setLocationType(null);
    if (setSelectedExternalId && locations) {
      setSelectedExternalId(locations[currentBuildingId]?.ExternalId);
    }

    if (sensorTypesShown?.length) {
      getAssets({
        locationId: currentBuildingId,
        floorId: selectedFloorId,
        enableSubLocationsLookUp: true,
        templateType: TemplateType.SENSOR,
      }).then((res) => {
        setSensors(res?.assets
          ?.filter((sensor: AssetInstance) => sensor.type === TemplateType.SENSOR
            || ((sensor.labels as SensorLabel[] | null)
              ?.some((label: SensorLabel) => sensorTypesShown.includes(label)) ?? false)) ?? []);
      });
    }

    const unsubscribe = navigation.addListener('focus', () => {
      GetEntireLocationStructure((updatedLocations) => dispatch(updateLocations(updatedLocations)));
    });
    return unsubscribe;
  }, [currentBuildingId, selectedFloor]);

  useEffect(() => {
    if (!selectedLocation) return;
    if (setLocationName) setLocationName(selectedLocation.Name);
    if (setSelectedExternalId) setSelectedExternalId(selectedLocation.ExternalId);
    if (setLocationCapacity) {
      setLocationCapacity((selectedLocation as (Building | Area | Space)).LocationCapacity);
    }
  }, [selectedLocation]);

  const SensorOnlineIndicator = ({ count }: { count: number }) => {
    const [iconHovered, setIconHovered] = useState<boolean>(false);
    const [tooltipHovered, setTooltipHovered] = useState<boolean>(false);
    const tooltipShown: boolean = tooltipHovered || iconHovered;

    return !!sensorTypesShown
      && <View style={{ overflow: 'visible' }}>
        <Pressable
          onHoverIn={() => setIconHovered(true)}
          onHoverOut={() => setIconHovered(false)}
          style={({ hovered }: PressableState) => hovered && Style.hovered}
          disabled={!(count > 0)}
        >
          <FontAwesome
            name={'circle-o'}
            size={12}
            color={count > 0 ? Colors.light.hereworksBlue500 : Colors.light.hereworksPrimaryGrey600}
            style={styles.circleIcon}
          />
        </Pressable>
        <View
          onPointerEnter={() => setTooltipHovered(true)}
          onPointerLeave={() => setTooltipHovered(false)}
          style={styles.tooltipContainer}
        >
          {tooltipShown && <View style={styles.tooltipBox}>
            <Text style={styles.tooltipText}>
              {count > 0 ? `${count} sensor(s) in this location`
                : 'No sensors found in this location'}
            </Text>
          </View>}
        </View>
      </View>;
  };

  const DropdownIcon = ({ expanded }: { expanded: boolean }) => <FontAwesome
    name={expanded ? 'angle-up' : 'angle-down'}
    size={24}
    color={Colors.light.hereworksBlue500}
  />;

  return <ScrollViewStyled
    nestedScrollEnabled
    children={<FlatList
      style={styles.buildingList}
      data={Object.keys(locations ?? {})}
      renderItem={({ item: buildingId }) => {
        if (hideOtherBuildings && buildingId !== currentBuildingId) return null;
        const buildingExpanded: boolean = expandedBuildingId === buildingId;
        const buildingSelected: boolean = selectedBuildingId === buildingId && !selectedFloorId;
        const building: Building = locations[buildingId];
        const floors = building.floors ?? {};
        const floorList: Floor[] = Object.values(floors);
        const floorKeys: string[] = Object.keys(floors);
        const buildingSensorCount: number = getSensorCount(mergeAssetCounts(
          floorList.map((floor: Floor) => {
            if (!floor) return null;
            const areaList: Area[] = Object.values(floor.areas ?? {});
            return mergeAssetCounts([
              floor.assetCountByLabel,
              ...areaList.map((area: Area) => {
                const spaceList: Space[] = Object.values(area.spaces ?? {});
                return mergeAssetCounts([
                  area.assetCountByLabel,
                  ...spaceList.map((space: Space) => space.assetCountByLabel),
                ]);
              }),
            ]);
          }),
        ));

        return <Pressable
          style={[styles.buildingContainer, buildingSelected && itemContainerSelectedStyle]}
          onHoverIn={(event) => event.stopPropagation()}
          onPress={() => {
            if (setSelectedBuildingId) setSelectedBuildingId(buildingId);
            setSelectedFloorId(null);
            setSelectedAreaId(null);
            setSelectedSpaceId(null);
            setExpandedBuildingId(buildingId);
            setExpandedFloorId(null);
            setExpandedAreaId(null);
            if (setSelectedSensorId) setSelectedSensorId(null);
            if (setSelectedSensorName) setSelectedSensorName(null);
            if (setLocationType) setLocationType(null);
          }}
        >
          {({ hovered: buildingHovered }: PressableState) => <>
            <View style={[Style.row, styles.buildingHeader]}>
              <View style={styles.icon}>
                {floorKeys.length > 0 ? <PressableStyled
                  onPress={() => setExpandedBuildingId(buildingExpanded ? null : buildingId)}
                >
                  <DropdownIcon expanded={buildingExpanded} />
                </PressableStyled> : null}
              </View>
              <Text style={[styles.buildingName, buildingHovered && styles.hoveredText]}>
                {building.Name}
              </Text>
              <SensorOnlineIndicator count={buildingSensorCount} />
            </View>
            {buildingExpanded && floorKeys.length > 0 && <ScrollView nestedScrollEnabled>
              <FlatList
                style={styles.list}
                data={floorKeys}
                renderItem={({ item: floorId }) => {
                  const floorExpanded: boolean = expandedFloorId === floorId;
                  const floorSelected: boolean = selectedFloorId === floorId && !selectedAreaId;
                  const floor: Floor = floors[floorId];
                  const areaList: Area[] = Object.values(floor.areas ?? {});
                  const floorSensorCount: number = getSensorCount(
                    mergeAssetCounts([
                      floor.assetCountByLabel,
                      ...areaList.map((area: Area) => {
                        const spaceList: Space[] = Object.values(area.spaces ?? {});
                        return mergeAssetCounts([
                          area.assetCountByLabel,
                          ...spaceList.map((space: Space) => space.assetCountByLabel),
                        ]);
                      }),
                    ]),
                  );
                  const areas = floor.areas ?? {};
                  const areaKeys: string[] = Object.keys(areas);

                  return <Pressable
                    style={[
                      styles.regionContainer,
                      !selectedSensorId && floorSelected && itemContainerSelectedStyle,
                    ]}
                    onHoverIn={(event) => event.stopPropagation()}
                    onPress={() => {
                      if (setSelectedBuildingId) setSelectedBuildingId(buildingId);
                      setSelectedFloorId(floorId);
                      setSelectedAreaId(null);
                      setSelectedSpaceId(null);
                      setExpandedBuildingId(buildingId);
                      setExpandedFloorId(floorId);
                      setExpandedAreaId(null);
                      if (setSelectedSensorId) setSelectedSensorId(null);
                      if (setSelectedSensorName) setSelectedSensorName(null);
                      if (setLocationType) setLocationType(null);
                      if (setLocationHasSensors) setLocationHasSensors(floorSensorCount > 0);
                    }}
                  >
                    {({ hovered: floorHovered }: PressableState) => <>
                      <View style={styles.locationItem}>
                        <View style={styles.icon}>
                          {areaKeys.length > 0 ? <PressableStyled
                            onPress={() => setExpandedFloorId(floorExpanded ? null : floorId)}
                          >
                            <DropdownIcon expanded={floorExpanded} />
                          </PressableStyled> : null}
                        </View>
                        <View style={styles.textSection}>
                          <Text style={[styles.name, floorHovered && styles.hoveredText]}>
                            {floor.Name}
                          </Text>
                          <Text style={[
                            styles.locationTypeLabel, floorHovered && styles.hoveredText,
                          ]}>
                            {'Floor'}
                          </Text>
                        </View>
                        <SensorOnlineIndicator count={floorSensorCount} />
                      </View>
                      {sensorsShown && floorExpanded && <FlatList
                        style={styles.spaceContainer}
                        data={sensors
                          ?.filter((sensor: AssetInstance) => sensor.locationId === floorId)}
                        renderItem={({ item: sensor }) => {
                          const sensorSelected: boolean = sensor.id === selectedSensorId;
                          return <Pressable
                            style={[
                              styles.regionContainer, styles.sensorItem,
                              sensorSelected && itemContainerSelectedStyle,
                            ]}
                            onHoverIn={(event) => event.stopPropagation()}
                            onPress={() => {
                              if (setSelectedSensorId) setSelectedSensorId(sensor.id);
                              if (setSelectedSensorName) setSelectedSensorName(sensor.name);
                              if (setSelectedBuildingId) setSelectedBuildingId(buildingId);
                              setSelectedFloorId(floorId);
                              setSelectedAreaId(null);
                              setSelectedSpaceId(null);
                            }}
                          >
                            {({ hovered: sensorHovered }) => <Text
                              style={[styles.sensorName, sensorHovered && styles.hoveredText]}
                            >
                              {sensor.name}
                            </Text>}
                          </Pressable>;
                        }}
                        keyExtractor={(sensor: AssetInstance) => `sensor_${sensor.id}`}
                      />}
                      {floorExpanded && areaKeys.length > 0 && <ScrollView nestedScrollEnabled>
                        <FlatList
                          style={styles.list}
                          data={areaKeys}
                          renderItem={({ item: areaId }) => {
                            const areaExpanded: boolean = expandedAreaId === areaId;
                            const areaSelected: boolean = selectedAreaId === areaId
                              && !selectedSpaceId;
                            const area: Area = areas[areaId];
                            const areaSensorCount: number = getSensorCount(mergeAssetCounts([
                              area.assetCountByLabel,
                              ...Object.values(area.spaces ?? {})
                                .map((space: Space) => space.assetCountByLabel),
                            ]));
                            const spaces = area.spaces ?? {};
                            const spaceKeys: string[] = Object.keys(spaces);

                            return <Pressable
                              style={[
                                styles.regionContainer,
                                areaSelected && !selectedSensorId && itemContainerSelectedStyle,
                              ]}
                              onHoverIn={(event) => event.stopPropagation()}
                              onPress={() => {
                                if (setSelectedBuildingId) setSelectedBuildingId(buildingId);
                                setSelectedFloorId(floorId);
                                setSelectedAreaId(areaId);
                                setSelectedSpaceId(null);
                                setExpandedBuildingId(buildingId);
                                setExpandedFloorId(floorId);
                                setExpandedAreaId(areaId);
                                if (setSelectedSensorId) setSelectedSensorId(null);
                                if (setSelectedSensorName) setSelectedSensorName(null);
                                if (setLocationType) setLocationType(area.LocationSubtype);
                                if (setLocationHasSensors) {
                                  setLocationHasSensors(areaSensorCount > 0);
                                }
                              }}
                            >
                              {({ hovered: areaHovered }: PressableState) => <>
                                <View style={styles.locationItem}>
                                  <View style={styles.icon}>
                                    {spaceKeys.length > 0 ? <PressableStyled
                                      onPress={() => {
                                        setExpandedAreaId(areaExpanded ? null : areaId);
                                      }}
                                    >
                                      <DropdownIcon expanded={areaExpanded} />
                                    </PressableStyled> : null}
                                  </View>
                                  <View style={styles.textSection}>
                                    <Text style={[styles.name, areaHovered && styles.hoveredText]}>
                                      {area.Name}
                                    </Text>
                                    <Text style={[
                                      styles.locationTypeLabel, areaHovered && styles.hoveredText,
                                    ]}>
                                      {area.LocationSubtype
                                        ? LocationSubtypeTitles[area.LocationSubtype] : 'Area'}
                                    </Text>
                                  </View>
                                  <SensorOnlineIndicator count={areaSensorCount} />
                                </View>
                                {(sensorsShown && areaExpanded) && <FlatList
                                  style={styles.spaceContainer}
                                  data={sensors?.filter((sensor: AssetInstance) => sensor
                                    .locationId === areaId)}
                                  renderItem={({ item: sensor }) => {
                                    const sensorSelected: boolean = sensor.id === selectedSensorId;
                                    return <Pressable
                                      style={[
                                        styles.regionContainer, styles.sensorItem,
                                        sensorSelected && itemContainerSelectedStyle,
                                      ]}
                                      onHoverIn={(event) => event.stopPropagation()}
                                      onPress={() => {
                                        if (setSelectedBuildingId) {
                                          setSelectedBuildingId(buildingId);
                                        }
                                        setSelectedFloorId(floorId);
                                        setSelectedAreaId(areaId);
                                        setSelectedSpaceId(null);
                                        if (setSelectedSensorId) setSelectedSensorId(sensor.id);
                                        if (setSelectedSensorName) {
                                          setSelectedSensorName(sensor.name);
                                        }
                                      }}
                                    >
                                      {({ hovered: sensorHovered }) => <Text
                                        style={[
                                          styles.sensorName, sensorHovered && styles.hoveredText,
                                        ]}
                                      >
                                        {sensor.name}
                                      </Text>}
                                    </Pressable>;
                                  }}
                                  keyExtractor={(sensor: AssetInstance) => `sensor_${sensor.id}`}
                                />}
                                {areaExpanded && spaceKeys.length > 0
                                  && <ScrollView nestedScrollEnabled>
                                    <FlatList
                                      style={styles.list}
                                      data={spaceKeys}
                                      renderItem={({ item: spaceId }) => {
                                        const spaceSelected: boolean = selectedSpaceId === spaceId;
                                        const space: Space = spaces[spaceId];
                                        const spaceSensorCount:
                                          number = getSensorCount(space.assetCountByLabel);

                                        return <Pressable
                                          style={[
                                            styles.regionContainer, styles.spaceContainer,
                                            spaceSelected && !selectedSensorId
                                            && itemContainerSelectedStyle,
                                          ]}
                                          onHoverIn={(event) => event.stopPropagation()}
                                          onPress={() => {
                                            if (setSelectedBuildingId) {
                                              setSelectedBuildingId(buildingId);
                                            }
                                            setSelectedFloorId(floorId);
                                            setSelectedAreaId(areaId);
                                            setSelectedSpaceId(spaceId);
                                            if (setLocationType) {
                                              setLocationType(space.LocationSubtype);
                                            }
                                            if (setSelectedSensorId) setSelectedSensorId(null);
                                            if (setSelectedSensorName) setSelectedSensorName(null);
                                            if (setLocationHasSensors) {
                                              setLocationHasSensors(spaceSensorCount > 0);
                                            }
                                          }}
                                        >
                                          {({ hovered: spaceHovered }: PressableState) => <View
                                            style={styles.locationItem}
                                          >
                                            <View style={styles.textSection}>
                                              <Text style={[
                                                styles.name, spaceHovered && styles.hoveredText,
                                              ]}>
                                                {space.Name}
                                              </Text>
                                              <Text
                                                style={[
                                                  styles.locationTypeLabel,
                                                  spaceHovered && styles.hoveredText,
                                                ]}
                                              >
                                                {space.LocationSubtype
                                                  ? LocationSubtypeTitles[space.LocationSubtype]
                                                  : 'Space'}
                                              </Text>
                                            </View>
                                            <SensorOnlineIndicator count={spaceSensorCount} />
                                          </View>}
                                        </Pressable>;
                                      }}
                                      keyExtractor={(spaceId: string) => `meeting_room_widget_space_${spaceId}`}
                                    />
                                  </ScrollView>}
                              </>}
                            </Pressable>;
                          }}
                          keyExtractor={(areaId: string) => `meeting_room_widget_area_${areaId}`}
                        />
                      </ScrollView>}
                    </>}
                  </Pressable>;
                }}
                keyExtractor={(floorId: string) => `meeting_room_widget_floor_${floorId}`}
              />
            </ScrollView>}
          </>}
        </Pressable>;
      }}
      keyExtractor={(buildingId: string) => `meeting_room_widget_building_${buildingId}`}
    />}
  />;
};

const styles = StyleSheet.create({
  buildingList: {
    paddingHorizontal: 12,
    paddingVertical: 16,
  },
  buildingContainer: {
    borderWidth: 1,
    borderColor: 'transparent',
    borderRadius: 8,
  },
  buildingHeader: {
    paddingVertical: 9,
    minHeight: 46,
    paddingStart: 6,
    paddingEnd: 12,
  },
  icon: {
    width: 24,
    height: 24,
    alignSelf: 'flex-start',
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: 1,
    marginEnd: 4,
  },
  buildingName: {
    fontSize: 14,
    lineHeight: 14,
    fontFamily: 'Poppins_400Regular',
    color: Colors.light.hereworksBlack500,
    flex: 1,
    marginStart: 4,
  },
  list: {
    marginStart: 8,
    paddingBottom: 8,
    paddingTop: 12,
  },
  name: {
    fontSize: 12,
    lineHeight: 12,
    fontFamily: 'Roboto_500Medium',
    color: Colors.light.hereworksBlack500,
  },
  regionContainer: {
    borderRadius: 8,
    borderWidth: 1,
    borderColor: 'transparent',
    marginStart: 6,
  },
  textSection: {
    flex: 1,
  },
  locationTypeLabel: {
    fontSize: 10,
    lineHeight: 14,
    fontFamily: 'Roboto_400Regular',
    color: Colors.light.hereworksBlack400,
    marginTop: 2,
  },
  locationItem: {
    ...Style.row,
    alignSelf: 'stretch',
    height: 44,
    paddingEnd: 12,
    marginStart: 6,
  },
  spaceContainer: {
    marginStart: 24,
    paddingStart: 6,
  },
  hoveredText: {
    color: Colors.light.hereworksBlue500,
  },
  circleIcon: {
    marginStart: 4,
  },
  sensorItem: {
    ...Style.row,
    paddingHorizontal: 8,
    paddingVertical: 5,
    borderRadius: 6,
  },
  sensorName: {
    fontSize: 11,
    lineHeight: 11,
    fontFamily: 'Roboto_500Medium',
    color: Colors.light.hereworksBlack400,
  },
  tooltipContainer: {
    height: '100%',
    position: 'absolute',
    paddingEnd: 16,
    end: 0,
  },
  tooltipBox: {
    borderRadius: 5,
    borderWidth: 1,
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderColor: Colors.light.hereworksBlue100,
    backgroundColor: '#FFF',
    width: 120,
    minHeight: 36,
    marginTop: -28,
  },
  tooltipText: {
    fontSize: 12,
    lineHeight: 18,
    fontFamily: 'Poppins_500Medium',
    letterSpacing: 0.15,
    color: Colors.light.hereworksBlue400,
  },
});

export default LocationSelector;
